import { useCallback, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Flex } from 'antd';
import { useFormik } from 'formik';

import dayjs from 'dayjs';
import { MainForm, RequiredNotification } from '@Components/index';
import FormItems from '@Components/MainForm/components/FormItems/FormItems';
import { resumptionRequestActions } from '@Features/EmployeeSelfService/requests/ResumptionRequests/store/resumptionRequestSlice';
import {
  useGetAttendanceTimeMutation,
  useGetResumptionLeavesMutation,
  useSaveDraftResumptionRequestMutation,
} from '@Features/EmployeeSelfService/requests/ResumptionRequests/store/api/resumptionRequestApiSlice';
import {
  useAppDispatch,
  useAppSelector,
  useNavigationToListRequests,
  useRequisitionInfoExistingReq,
} from '@Hooks/index';
import {
  useCancelHrmRequestMutation,
  useDeleteHrmRequestMutation,
  useGetHrmRequestFormQuery,
  useSubmitHrmRequestMutation,
} from '@Store/api/basicHrmApiSlice';
import { currentUserSelector } from '@Store/selectors/userInfoSelector';
import { NotificationContext } from '@Context/NotificationContext';
import { RequestError } from '@Types/api/form';
import { FooterActions } from '@Enum/index';
import { OptionNum } from '@Components/types/options';
import { ResumptionStatusCodes } from '@Enum/api/ResumptionStatusCodes.enum';
import { getEditFormType } from '@Features/utils/getEditFormType';
import { validateForm } from '@Features/utils/validateForm';
import { getResumptionWorkShiftDict } from '@Store/selectors/dictionarySelector';
import { transformDictToOptionsNum } from '@Utils/transformToOptions';
import { ResumptionLeavesResponse } from '@Types/api/form/resumptionRequest/ResumpationLeaves';
import RequestDetailsSection from '../components/RequestDetailsSection';
import { initialFormValues } from '../initialValues';
import { getValidationSchema } from '../Create/validationSchema';
import { ResumptionRequestFormValues } from '../../types';
import { ATTENDANCE_TIME_ERROR } from '../constants';

const ResumptionRequestEditForm: React.FC = () => {
  const {
    editSuccessNotification,
    errorNotification,
    deleteSuccessNotification,
    submitSuccessNotification,
    cancelSuccessNotification,
  } = useContext(NotificationContext);
  const { requestId } = useParams();

  const [attendanceTime, setAttendanceTime] = useState('');
  const [resumptionLeaves, setResumptionLeaves] =
    useState<ResumptionLeavesResponse>();
  const [showDeleteNotification, setShowDeleteNotification] = useState(false);
  const [disabledSaveForm, setDisableSaveForm] = useState(false);
  const [resumingFromOptions, setResumingFromOptions] = useState<OptionNum[]>(
    []
  );
  const dispatch = useAppDispatch();
  const resumptionWorkShifts = useAppSelector(getResumptionWorkShiftDict);
  const [getAttendanceTime] = useGetAttendanceTimeMutation();
  const [submitResumptionRequest] = useSubmitHrmRequestMutation();
  const [saveDraftResumptionRequest] = useSaveDraftResumptionRequestMutation();
  const [getResumptionLeaves] = useGetResumptionLeavesMutation();
  const { data: editDataForm } = useGetHrmRequestFormQuery(requestId!, {
    skip: !requestId,
    refetchOnMountOrArgChange: true,
  });
  const [deleteRequest] = useDeleteHrmRequestMutation();
  const [cancelRequest] = useCancelHrmRequestMutation();
  const requisitionInfo = useRequisitionInfoExistingReq(
    editDataForm,
    'Resumption'
  );

  const { handleBackToListRequests } = useNavigationToListRequests();
  const currentUser = useAppSelector(currentUserSelector);

  useEffect(
    () => () => {
      dispatch(resumptionRequestActions.setResumptionRequest(null));
    },
    [requestId]
  );

  useEffect(() => {
    if (currentUser.empNumber) {
      getResumptionLeaves({
        empNumber: currentUser.empNumber,
      })
        .unwrap()
        .then((res) => {
          setResumptionLeaves(res);
        });
    }
  }, [currentUser.empNumber]);

  useEffect(() => {
    if (resumptionLeaves?.ResDates) {
      const resumptionOptions = resumptionLeaves?.ResDates.map(
        (leave) =>
          ({
            label: leave.LeaveData,
            value: Number(leave.ReqID),
          }) as OptionNum
      );

      setResumingFromOptions(resumptionOptions);
    }
  }, [resumptionLeaves]);

  const handleSubmit = useCallback(
    (values: ResumptionRequestFormValues): void => {
      const submitForm = async (): Promise<void> => {
        const body = {
          requestId: editDataForm?.requestId,
          empNumber: editDataForm?.empNumber as string,
          currentLocation: values.currentLocation,
          leaveTypeID: values.resumingFromLeaveRequestId as number,
          startDate: values.resumingOnDate,
          ticketRequired: values.workShift,
          remarks: values.remarks,
          destination: attendanceTime,
          totDays: values.totDays,
        };

        setDisableSaveForm(true);

        try {
          const {
            createdDate,
            reqStatusId,
            requestId: reqId,
          } = await saveDraftResumptionRequest(body).unwrap();

          dispatch(
            resumptionRequestActions.setResumptionRequest({
              createdDate,
              requestId: reqId,
              reqStatusId,
            })
          );

          await submitResumptionRequest(String(reqId)).unwrap();

          submitSuccessNotification(String(reqId));

          setDisableSaveForm(false);
          handleBackToListRequests();
        } catch (error) {
          const message = (error as RequestError)?.data;
          errorNotification(message);

          setDisableSaveForm(false);
        }
      };

      submitForm();
    },
    [handleBackToListRequests, editDataForm, attendanceTime]
  );

  const schema = getValidationSchema();
  const formik = useFormik({
    initialValues: initialFormValues,
    enableReinitialize: true,
    validationSchema: schema,
    onSubmit: handleSubmit,
  });

  const onResumingFromChange = useCallback(
    (leaveRequestId: number) => {
      const leaveItem = resumptionLeaves?.ResDates?.find(
        (leave) => Number(leave.ReqID) === leaveRequestId
      );

      if (leaveItem) {
        const leaveSplittedDate = leaveItem?.LeaveData.split(':');
        const leaveEndDate = leaveSplittedDate[leaveSplittedDate.length - 1];
        formik.setFieldValue('resumingFromLeaveRequestEndDate', leaveEndDate);
        formik.setFieldValue('totDays', leaveItem?.ReqIDSync);
      }
    },
    [resumptionLeaves, formik]
  );

  useEffect(() => {
    if (formik.values.resumingOnDate) {
      getAttendanceTime(
        dayjs(formik.values.resumingOnDate)
          ?.startOf('day')
          ?.format('YYYY-MM-DD')
      )
        .unwrap()
        .then((resp) => {
          const validValue = resp.intime || resp.outime;
          setAttendanceTime(validValue);
        })
        .catch(() => {
          formik.setFieldError('resumingOnDate', ATTENDANCE_TIME_ERROR);
        });
    }
  }, [formik.values.resumingOnDate]);

  const handleSave = useCallback(async (): Promise<void> => {
    const isFormValid = await validateForm(schema, formik);

    if (!isFormValid) return;

    const body = {
      requestId: editDataForm?.requestId,
      empNumber: editDataForm?.empNumber as string,
      currentLocation: formik.values.currentLocation,
      leaveTypeID: formik.values.resumingFromLeaveRequestId as number,
      startDate: formik.values.resumingOnDate,
      ticketRequired: formik.values.workShift,
      remarks: formik.values.remarks,
      destination: attendanceTime,
      totDays: formik.values.totDays,
    };

    setDisableSaveForm(true);

    try {
      const {
        createdDate,
        reqStatusId,
        requestId: reqId,
      } = await saveDraftResumptionRequest(body).unwrap();

      dispatch(
        resumptionRequestActions.setResumptionRequest({
          createdDate,
          requestId: reqId,
          reqStatusId,
        })
      );

      editSuccessNotification(String(reqId));

      setDisableSaveForm(false);
    } catch (error) {
      const message = (error as RequestError)?.data;
      errorNotification(message);

      setDisableSaveForm(false);
    }
  }, [editDataForm, formik, schema, attendanceTime]);

  const showDeleteRequestPopup = (): void => {
    setShowDeleteNotification(true);
  };

  const handleDeleteRequest = useCallback((): void => {
    if (!editDataForm?.requestId) {
      return;
    }

    const reqId = String(editDataForm.requestId);

    deleteRequest(reqId)
      .unwrap()
      .then(() => {
        deleteSuccessNotification(reqId);

        handleBackToListRequests();
      })
      .catch(({ data }) => {
        errorNotification(data);
      });
  }, [editDataForm, handleBackToListRequests]);

  const handleCancel = useCallback(() => {
    if (!editDataForm?.requestId) {
      return;
    }

    const reqId = String(editDataForm.requestId);

    cancelRequest(reqId)
      .unwrap()
      .then(() => {
        cancelSuccessNotification(reqId);

        handleBackToListRequests();
      })
      .catch(({ data }) => {
        errorNotification(data);
      });
  }, [handleBackToListRequests, editDataForm]);

  const handleFooterAction = useCallback(
    (action: FooterActions) => {
      if (action === FooterActions.CANCEL) {
        handleCancel();
      }

      if (action === FooterActions.SAVE_AS_DRAFT) {
        handleSave();
      }

      if (action === FooterActions.DELETE_REQUEST) {
        showDeleteRequestPopup();
      }

      if (action === FooterActions.BACK_TO_REQUESTS) {
        handleBackToListRequests();
      }
    },
    [handleBackToListRequests, handleSave, handleCancel]
  );

  const isOpenStatus =
    !editDataForm || editDataForm.reqStatusId === ResumptionStatusCodes.OPEN;
  const formType = getEditFormType(isOpenStatus);

  return (
    <Flex gap={10} vertical style={{ height: '100%' }}>
      <MainForm
        disabledSaveForm={disabledSaveForm}
        handleSubmit={formik.handleSubmit}
        userInfo={currentUser}
        requisitionInfo={requisitionInfo}
        onFooterAction={handleFooterAction}
        formType={formType}
      >
        <FormItems>
          <RequestDetailsSection
            label="Request Details"
            formik={formik}
            formApproval={false}
            resumingFromOptions={resumingFromOptions}
            onResumingFromChange={onResumingFromChange}
            attendanceTime={attendanceTime}
            resumptionWorkShiftOptions={transformDictToOptionsNum(
              resumptionWorkShifts
            )}
          />
        </FormItems>
      </MainForm>
      <RequiredNotification
        title="Delete Request"
        message={`Are you sure you want to delete request no.${requestId}?`}
        isModalOpen={showDeleteNotification}
        textSuccessBtn="Yes, Delete"
        textCancelBtn="No, Thanks"
        handleCancel={() => setShowDeleteNotification(false)}
        handleSuccess={handleDeleteRequest}
        isDeleteNotification
      />
    </Flex>
  );
};

export default ResumptionRequestEditForm;
